import { useCallback, useRef, useEffect, useState } from "react";
import ReactFlow, {
    Background,
    useReactFlow,
    Controls,
    ControlButton,
    BackgroundVariant,
    SelectionMode,
    ConnectionLineType,
    ReactFlowInstance,
    Node,
} from "reactflow";


// Styles
import "reactflow/dist/style.css";
import "../../styles/proximity-edges.css";

// Contexts & Providers
import { CanvasContext } from '../../contexts/CanvasContext';
import { useFlowActions } from '../../contexts/FlowActionsContext';
import { useActionSnackbar } from '../../contexts/ActionSnackbarContext'; // Import snackbar hook
// Store & State Management
import useStore, { FlowState } from '../../store/useStore';
import { usePlaybookStore, PlaybookState, PlaybookNodeData } from '../../store/usePlaybookStore'; // Import Playbook store, state type and PlaybookNodeData
import { AnyNodeData, CustomNode } from "../../types"; // Import AnyNodeData, CustomNode
// Import batch add functions
const addNodesAtomicallyProject = useStore.getState().addNodesAtomically;
const addNodesAtomicallyPlaybook = usePlaybookStore.getState().addNodesAtomically;

// Hooks
import { useHotkeys } from '../../hooks/useHotkeys';
import { useCanvasState } from './useCanvasState';
import { useUndoRedo } from './useUndoRedo';
import { useNodeDragHandlers } from './useNodeDragHandlers';
import { useConnectionHandlers, ConnectionHandlerProps } from './useConnectionHandlers'; // Import props type
import { useDropHandlers, DropHandlerProps } from './useDropHandlers'; // Import props type
import { useInteractionHandlers, InteractionHandlerProps } from './useInteractionHandlers'; // Import props type
import { useChangeHandlers, ChangeHandlerProps } from './useChangeHandlers'; // Import props type
import { useClipboard } from '../../hooks/useClipboard'; // Import the clipboard hook
import { useClipboardImage } from '../../hooks/useClipboardImage'; // Import the clipboard image hook

// Components & Node/Edge Types
import HelperLines from "./HelperLines";
import { nodeTypes as projectNodeTypes } from "../../node-types"; // Renamed import
import { playbookNodeTypes } from "../../playbook-node-types"; // Import playbook nodes
import LeftPanel from "@/components/left-panel/LeftPanel";
import RightPanel from "@/components/RightPanel";
import { CustomEdge } from './edges'; // Keep existing custom edge
import EditableEdge from '../edge/EditableEdge'; // Import the new editable edge
import StraightEdgeWithLabel from '../edge/StraightEdgeWithLabel'; // Import wrapper
import StepEdgeWithLabel from '../edge/StepEdgeWithLabel';       // Import wrapper
import SmoothStepEdgeWithLabel from '../edge/SmoothStepEdgeWithLabel'; // Import wrapper


// Combine node types - will be selected based on mode later
const defaultNodeTypes = { ...projectNodeTypes };
// Add the new editable edge type and standard types
const edgeTypes = {
    custom: CustomEdge,
    'editable-edge': EditableEdge, // For default/bezier shapes (handled by data.shape)
    straight: StraightEdgeWithLabel,
    step: StepEdgeWithLabel,
    smoothstep: SmoothStepEdgeWithLabel,
};

// Define modes
export type CanvasMode = 'project' | 'playbook'; // Export the type

interface CanvasProps {
    isEmbedded?: boolean; // Keep isEmbedded prop
    mode?: CanvasMode; // Add mode prop
}

const Canvas = ({ isEmbedded = false, mode = 'project' }: CanvasProps) => { // Default to project mode
    // --- Refs ---
    const reactFlowWrapper = useRef<HTMLDivElement | null>(null);

    // --- Basic Canvas State ---
    const {
        isEmbedMode, // Use the state from the hook
        isDarkMode,
        isPanning,
        setIsPanning, // Get setter from hook
        isSpacePressed,
        isShiftPressed,
        isAltPressed, // Get Alt key state
    } = useCanvasState(); // Use the custom hook

    // --- Store Selection & State Access ---
    const useCurrentStore = mode === 'playbook' ? usePlaybookStore : useStore;
    // Fetch the whole state - simpler for type safety with union types
    const storeState = useCurrentStore() as FlowState | PlaybookState;

    // *** Access State/Actions with Type Guards ***
    const nodes = 'playbookNodes' in storeState ? storeState.playbookNodes : storeState.nodes;
    const edges = 'playbookEdges' in storeState ? storeState.playbookEdges : storeState.edges;
    const selectedNode = storeState.selectedNode; // Common property
    const selectedEdge = storeState.selectedEdge; // Common property
    const viewport = storeState.viewport; // Common property
    const helperLineHorizontal = storeState.helperLineHorizontal; // Common property
    const helperLineVertical = storeState.helperLineVertical; // Common property
    const currentProject = mode === 'project' && 'currentProject' in storeState ? storeState.currentProject : null;

    // Common Actions (assuming signatures are compatible or handled in hooks)
    const setViewport = storeState.setViewport;
    const setSelectedNode = storeState.setSelectedNode;
    const setSelectedEdge = storeState.setSelectedEdge;
    const setEdges = storeState.setEdges;
    const setNodes = storeState.setNodes;
    const onNodesChange = storeState.onNodesChange;
    const onEdgesChange = storeState.onEdgesChange;
    const deleteNode = storeState.deleteNode;
    const deleteEdge = storeState.deleteEdge;

    // Mode-specific actions (use type guards)
    const addNode = mode === 'project' && 'addNode' in storeState ? storeState.addNode : undefined;
    const addPlaybookNode = mode === 'playbook' && 'addPlaybookNode' in storeState ? storeState.addPlaybookNode : undefined;
    const recalculateClusterLatencies = mode === 'project' && 'recalculateClusterLatencies' in storeState ? storeState.recalculateClusterLatencies : undefined;
    // *** END Access State/Actions with Type Guards ***


    // --- React Flow Instance & Context ---
    const { fitView } = useReactFlow(); // resetSelectedElements will be accessed via reactFlowInstanceLocal
    const { setFitViewFunction } = useFlowActions();
    const { showSnackbar } = useActionSnackbar(); // Get snackbar function
    const [reactFlowInstanceLocal, setReactFlowInstanceLocal] = useState<ReactFlowInstance | null>(null);

    // --- Hooks for Handlers ---
    // Pass state/actions accessed via type guards
    const { undo, redo, canUndo, canRedo, takeSnapshot, onInit: onInitUndoRedo } = useUndoRedo(); // TODO: Adapt for current store

    // Props for connection handlers
    const connectionHandlerProps: ConnectionHandlerProps = {
        nodes: nodes, // Use guarded nodes
        edges: edges, // Use guarded edges
        setEdges: setEdges, // Pass action
        recalculateClusterLatencies: recalculateClusterLatencies, // Pass guarded action
    };
    const { onConnect } = useConnectionHandlers(isEmbedded, takeSnapshot, mode, connectionHandlerProps);

    // Determine the correct add node function based on mode
    const addNodeFunction = mode === 'project' ? addNode : addPlaybookNode;
    const addNodesFunction = mode === 'project' ? addNodesAtomicallyProject : addNodesAtomicallyPlaybook;


    // Props for node drag handlers
    const { onNodeDragStart, onNodeDrag, onNodeDragStop } = useNodeDragHandlers(
        isEmbedded,
        isShiftPressed,
        isAltPressed,
        takeSnapshot,
        addNodeFunction as (node: CustomNode | Node<PlaybookNodeData>) => void, // Cast for single node add
        addNodesFunction as (nodes: Node[] | Node<PlaybookNodeData>[]) => void, // Cast for batch node add
        mode
    );

    // Props for drop handlers
    const dropHandlerProps: DropHandlerProps = {
        isEmbedded,
        reactFlowWrapperRef: reactFlowWrapper,
        mode,
        addNode: addNode, // Pass guarded action
        addPlaybookNode: addPlaybookNode, // Pass guarded action
        setSelectedNode: setSelectedNode, // Pass action
        nodes: nodes, // Pass guarded nodes
    };
    const { onDragOver, onDrop } = useDropHandlers(dropHandlerProps);

    // Props for interaction handlers
    const interactionHandlerProps: InteractionHandlerProps = {
        isEmbedded,
        mode,
        setSelectedEdge: setSelectedEdge, // Pass action
        setSelectedNode: setSelectedNode, // Pass action
        setNodes: setNodes,               // Pass action
        nodes: nodes,                     // Pass guarded nodes
        edges: edges,                     // Pass guarded edges *** ADDED ***
        setEdges: setEdges,               // Pass action *** ADDED ***
        selectedEdge: selectedEdge,       // Pass selected edge state
    };
    const interactionHandlers = useInteractionHandlers(interactionHandlerProps);
    const originalOnPaneClick = interactionHandlers.onPaneClick;

    // Simplified pane click handler to avoid infinite update loops
    const handlePaneClick = useCallback((event: React.MouseEvent) => {
        // Only call the original onPaneClick from useInteractionHandlers
        // This will handle deselecting nodes in the store
        if (originalOnPaneClick) {
            originalOnPaneClick(event);
        }
        
        // We're no longer calling onSelectionChange here to avoid potential circular updates
        // The original onPaneClick already handles deselection in the store
    }, [originalOnPaneClick]); // Simplified dependencies

    // Props for change handlers
    const changeHandlerProps: ChangeHandlerProps = {
        isEmbedded,
        selectedNodeIdRef: interactionHandlers.selectedNodeIdRef, // Corrected access
        mode,
        onNodesChange: onNodesChange,     // Pass action
        onEdgesChange: onEdgesChange,     // Pass action
        setSelectedNode: setSelectedNode, // Pass action
        nodes: nodes,                     // Pass guarded nodes
    };
    const { handleNodesChange, handleEdgesChange } = useChangeHandlers(changeHandlerProps);

    // --- Other Hooks ---
    const { registerHotkey } = useHotkeys(); // Restore the hook
    useClipboard(); // Call the clipboard hook to register its hotkeys
    const { copySelectedAsImage } = useClipboardImage(reactFlowInstanceLocal); // Pass reactFlowInstanceLocal

    // Register Cmd+Shift+P hotkey for copy as image
    useEffect(() => {
        registerHotkey({
            key: "p", // Changed from "c"
            modifiers: ["meta", "shift"], // Changed from ["meta", "alt"]
            description: "Copy selected as Image (Cmd+Shift+P)", // Updated description
            handler: (e: KeyboardEvent) => {
                e.preventDefault();
                copySelectedAsImage();
            },
            preventDefault: true,
        });
        // Cleanup function to unregister hotkey if needed, though useHotkeys might handle this
        // return () => { /* unregister logic if necessary */ };
    }, [registerHotkey, copySelectedAsImage]);

    // --- Combined onInit ---
    const onInit = useCallback((instance: ReactFlowInstance) => {
        // Store the instance locally
        setReactFlowInstanceLocal(instance);
        
        // Pass the instance to the undo/redo hook
        // This will store it in the Zustand store for use in undo/redo operations
        onInitUndoRedo(instance);
    }, [onInitUndoRedo]);

    // --- Effects ---
    // Register fitView function
    useEffect(() => {
        if (fitView) {
            setFitViewFunction(fitView);
        }
    }, [fitView, setFitViewFunction]);

    // Recalculate latencies only in project mode
    useEffect(() => {
        if (mode === 'project' && recalculateClusterLatencies) {
            recalculateClusterLatencies();
        }
    }, [nodes, edges, mode, recalculateClusterLatencies]); // Depend on selected state/actions

    // Delete Hotkeys - Re-implemented with setTimeout for both keys
    useEffect(() => {
        const deleteHandler = () => {
            // Actions are available from storeState or guarded access
            if (selectedEdge && deleteEdge && setSelectedEdge) {
                deleteEdge(selectedEdge);
                setSelectedEdge(null);
            }

            const selectedNodesFromStore = nodes.filter((node: Node) => node.selected); // Use guarded nodes

            if (selectedNodesFromStore.length > 0 && deleteNode && setSelectedNode) {
                selectedNodesFromStore.forEach((node: Node) => {
                    deleteNode(node.id);
                });
                setSelectedNode(null);
                interactionHandlers.selectedNodeIdRef.current = null; // Corrected access
            } else if (selectedNode && deleteNode && setSelectedNode) { // Fallback
                deleteNode(selectedNode.id);
                setSelectedNode(null);
                interactionHandlers.selectedNodeIdRef.current = null; // Corrected access
            }
        };

        // Wrapper function to apply the timeout
        const delayedDeleteHandler = () => {
            setTimeout(deleteHandler, 0); // Wrap the original handler in a timeout
        };

        // Register both Delete (Fn+Backspace) and Backspace with the delayed handler
        registerHotkey({ key: "delete", description: "Delete selected", handler: delayedDeleteHandler, preventDefault: true });
        registerHotkey({ key: "Backspace", description: "Delete selected", handler: delayedDeleteHandler, preventDefault: true });

    }, [registerHotkey, selectedEdge, selectedNode, interactionHandlers.selectedNodeIdRef, nodes, deleteEdge, setSelectedEdge, deleteNode, setSelectedNode, takeSnapshot]); // Added takeSnapshot dependency, Corrected access in dependency array

    // Nudge Hotkeys for selected nodes
    useEffect(() => {
        const internalHandleNudge = (deltaX: number, deltaY: number) => {
            const selectedNodesFromStore = nodes.filter((n: Node) => n.selected);
            if (selectedNodesFromStore.length === 0) {
                return;
            }

            // Take snapshot *before* applying the nudge
            takeSnapshot(); 

            const updatedNodes = nodes.map((node: Node) => {
                if (node.selected) {
                    // For child nodes, their position is relative to the parent.
                    // For non-child nodes, it's absolute.
                    // This logic correctly updates the position regardless.
                    return {
                        ...node,
                        position: {
                            x: node.position.x + deltaX,
                            y: node.position.y + deltaY,
                        },
                    };
                }
                return node;
            });

            setNodes(updatedNodes);
        };

        // Register hotkeys for 1px nudge
        registerHotkey({ key: "ArrowUp", description: "Nudge selection up (1px)", handler: () => internalHandleNudge(0, -1), preventDefault: true });
        registerHotkey({ key: "ArrowDown", description: "Nudge selection down (1px)", handler: () => internalHandleNudge(0, 1), preventDefault: true });
        registerHotkey({ key: "ArrowLeft", description: "Nudge selection left (1px)", handler: () => internalHandleNudge(-1, 0), preventDefault: true });
        registerHotkey({ key: "ArrowRight", description: "Nudge selection right (1px)", handler: () => internalHandleNudge(1, 0), preventDefault: true });

        // Register hotkeys for 10px nudge (Shift + Arrow)
        // Using "Shift+Key" convention, which is common.
        registerHotkey({ key: "Shift+ArrowUp", description: "Nudge selection up (10px)", handler: () => internalHandleNudge(0, -10), preventDefault: true });
        registerHotkey({ key: "Shift+ArrowDown", description: "Nudge selection down (10px)", handler: () => internalHandleNudge(0, 10), preventDefault: true });
        registerHotkey({ key: "Shift+ArrowLeft", description: "Nudge selection left (10px)", handler: () => internalHandleNudge(-10, 0), preventDefault: true });
        registerHotkey({ key: "Shift+ArrowRight", description: "Nudge selection right (10px)", handler: () => internalHandleNudge(10, 0), preventDefault: true });

    }, [registerHotkey, nodes, setNodes, takeSnapshot]); // Removed isShiftPressed from dependencies


    // Global Enter key handler for search focus (only relevant in project mode?)
    useEffect(() => {
        const handleBodyKeyDown = (event: KeyboardEvent) => {
            const isUndo = (event.ctrlKey || event.metaKey) && event.key === 'z' && !event.shiftKey;
            const isRedo = (event.ctrlKey || event.metaKey) && event.key === 'z' && event.shiftKey;
            if (isUndo || isRedo) return; // Handled by useUndoRedo

            if (event.key === 'Enter') {
                const target = event.target as HTMLElement;
                const isInFormField = target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement || target.isContentEditable;
                const isCanvasPage = window.location.pathname.includes('canvas');
                const noDialogsOpen = !document.querySelector('[role="dialog"]') && !document.querySelector('[role="menu"]');

                if (!isInFormField && isCanvasPage && noDialogsOpen) {
                    const searchInput = document.querySelector('.search-input') as HTMLInputElement;
                    if (searchInput) {
                        searchInput.focus();
                        event.preventDefault();
                        event.stopPropagation();
                    }
                }
            }
        };
        document.body.addEventListener('keydown', handleBodyKeyDown);
        return () => document.body.removeEventListener('keydown', handleBodyKeyDown);
    }, []); // No dependencies needed

    // --- Direct Callbacks ---
    const onEdgeDeleteCallback = useCallback(() => {
        // This callback might still be useful if edges are deleted via other means
        // or if React Flow's onEdgesDelete needs specific handling.
        // For now, ensure recalculation happens if needed.
        // NOTE: We are no longer using onNodesDelete
        if (mode === 'project' && recalculateClusterLatencies) {
            setTimeout(() => {
                recalculateClusterLatencies();
            }, 0);
        }
    }, [mode, recalculateClusterLatencies]); // Update dependencies

    // Select node types based on mode
    const nodeTypesToUse = mode === 'playbook' ? playbookNodeTypes : defaultNodeTypes;

    // Find the actual selected edge object using the ID from the store
    const selectedEdgeObject = edges.find(edge => edge.id === selectedEdge) || null;

    // --- Error Handling ---
    // React Flow onError signature: (code: string, message: string, params?: any) => void
    // Note: The actual signature might vary slightly based on version, but code/message are common.
    // We'll use a simplified (code, message) for now. Adjust if needed based on console logs.
    const handleFlowError = useCallback((code: string, message: string) => {
        console.error(`React Flow Error Code: ${code}`, message); // Log the code and message

        // Check for the specific "Parent node not found" error code '002'
        // Sometimes the code might be embedded in the message like "[002] Parent node ... not found"
        if (code === '002' || message.includes('[002]')) {
            showSnackbar("Error: Could not group nodes. Parent node reference lost. Please try again."); // Removed second argument
        } else {
            // Show a generic message for other errors
            showSnackbar(`An unexpected error occurred: ${message}`); // Removed second argument
        }
    }, [showSnackbar]); // Dependency on showSnackbar


    return (
        <CanvasContext.Provider value={{
            reactFlowWrapper,
            reactFlowInstance: reactFlowInstanceLocal || undefined, // Convert null to undefined
            fitView
        }}>
            <div className="dndflow" ref={reactFlowWrapper}>
                {/* Removed inner focusable div as ReactFlow container handles focus */}
                <div
                    className={`reactflow-wrapper ${isPanning ? 'is-panning' : ''} ${isSpacePressed ? 'can-pan' : ''} ${isDarkMode ? 'dark' : ''}`}
                    style={{
                        width: "100%",
                        height: isEmbedded ? "100vh" : "calc(100vh - 56px)" // Adjust height based on embedded status
                    }}
                >
                    <ReactFlow
                        proOptions={{ hideAttribution: true }}
                        minZoom={0.1}
                        maxZoom={2.5}
                        selectionMode={SelectionMode.Full}
                        nodes={nodes} // Use guarded nodes
                        edges={edges} // Use guarded edges
                        nodeTypes={nodeTypesToUse}
                        edgeTypes={edgeTypes}
                        onNodesChange={handleNodesChange} // Use handler from hook
                        onEdgesChange={handleEdgesChange} // Use handler from hook
                        onConnect={onConnect} // Use handler from hook
                        onNodeClick={interactionHandlers.onNodeClick} // Use handler from hook
                        onPaneClick={handlePaneClick} // Use wrapped handler
                        onNodeDragStart={onNodeDragStart} // Use handler from hook
                        onNodeDrag={onNodeDrag} // Use handler from hook
                        onNodeDragStop={onNodeDragStop} // Use handler from hook
                        onSelectionChange={interactionHandlers.onSelectionChange} // Use handler from hook
                        onEdgeClick={interactionHandlers.onEdgeClick} // Use handler from hook
                        // onNodesDelete={handleNodesDelete} // Removed - using custom hotkeys again
                        onEdgesDelete={onEdgeDeleteCallback} // Keep existing edge delete handler
                        onInit={onInit} // Use combined init
                        onDrop={onDrop} // Use handler from hook
                        onDragOver={onDragOver} // Use handler from hook
                        defaultViewport={viewport || { x: 0, y: 0, zoom: 1 }} // Use guarded viewport
                        onMove={(_event, vp) => setViewport(vp)} // Use action
                        connectionLineType={ConnectionLineType.Bezier}
                        multiSelectionKeyCode={'Shift'} // *** ADDED: Explicitly use Shift for multi-select ***
                        panActivationKeyCode="Space"
                        nodesDraggable={!isEmbedded}
                        nodesConnectable={!isEmbedded}
                        edgesUpdatable={!isEmbedded}
                        elementsSelectable={true}
                        selectionOnDrag
                        edgesFocusable={false}
                        panOnDrag={[1, 2]}
                        panOnScroll
                        onMoveStart={() => { if (isSpacePressed) setIsPanning(true); }}
                        onMoveEnd={() => { if (isSpacePressed) setIsPanning(false); }}
                        onlyRenderVisibleElements // Add this prop for performance
                        snapToGrid={true}
                        snapGrid={[1, 1]}
                        onError={handleFlowError} // Add the error handler prop
                    >
                        <svg> {/* Add this wrapper SVG tag */}
                           <defs>
                               <marker
                                   id="reactflow__arrowclosed"
                                   viewBox="-10 -10 20 20"
                                   markerWidth="12.5"
                                   markerHeight="12.5"
                                   refX="10"
                                   refY="0"
                                   orient="auto"
                               >
                                   <polyline
                                       stroke="#000"
                                       strokeLinecap="round"
                                       strokeLinejoin="round"
                                       strokeWidth="1"
                                       points="-2,-4 0,0 -2,4"
                                       fill="none"
                                   />
                               </marker>
                           </defs>
                        </svg>
                        <Controls
                            className="react-flow__controls-visible"
                            showZoom={true}
                            showFitView={true}
                            showInteractive={true}
                            position="bottom-right"
                        >
                            {/* Updated Control buttons */}
                            <ControlButton
                                onClick={() => {
                                    if (!undo()) { // Assuming undo() from useUndoRedo might need adjustment
                                        showSnackbar('Nothing to undo');
                                    }
                                }}
                                title="Undo (Cmd+Z)"
                                aria-label="Undo last action"
                                disabled={!canUndo} // Corrected: Remove parentheses
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 7v6h6"/><path d="M21 17a9 9 0 0 0-9-9 9 9 0 0 0-6 2.3L3 13"/></svg>
                            </ControlButton>
                            <ControlButton
                                onClick={() => {
                                    if (!redo()) { // Assuming redo() from useUndoRedo might need adjustment
                                        showSnackbar('Nothing to redo');
                                    }
                                }}
                                title="Redo (Cmd+Shift+Z)"
                                aria-label="Redo last undone action"
                                disabled={!canRedo} // Corrected: Remove parentheses
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 7v6h-6"/><path d="M3 17a9 9 0 0 1 9-9 9 9 0 0 1 6 2.3l3-2.3"/></svg>
                            </ControlButton>
                            {/* Removed image capture button from default toolbar - now available in GroupConfigBox */}
                        </Controls>
                        <Background
                            variant={BackgroundVariant.Dots}
                            gap={16}
                            size={1}
                            color={isDarkMode ? '#525365' : '#9294aa'}
                            className="dark:bg-gray-900"
                        />
                        {(() => {
                            const draggingNode = nodes.find((n: any) => n.dragging);
                            let groupBounds = null;
                            if (draggingNode?.parentId) {
                                const groupNode = nodes.find((n: any) => n.id === draggingNode.parentId);
                                if (groupNode) {
                                    groupBounds = {
                                        x: groupNode.position.x,
                                        y: groupNode.position.y,
                                        width: groupNode.width ?? 0,
                                        height: groupNode.height ?? 0,
                                    };
                                }
                            }
                            return (
                                <HelperLines
                                    horizontal={helperLineHorizontal !== undefined ? helperLineHorizontal : null}
                                    vertical={helperLineVertical !== undefined ? helperLineVertical : null}
                                    groupBounds={groupBounds}
                                    fixedLines={('fixedLines' in storeState && Array.isArray(storeState.fixedLines)) ? storeState.fixedLines : []}
                                    spacingLines={('spacingLines' in storeState && Array.isArray(storeState.spacingLines)) ? storeState.spacingLines : []}
                                />
                            );
                        })()}
                        {/* Left/Right Panels */}
                        {!isEmbedded && mode === 'project' && addNode && ( // Use guarded action
                            <LeftPanel
                                currentProject={currentProject?.id ?? ""} // Use guarded state
                                onAdd={addNode} // Use guarded action
                                isPreviewMode={isEmbedded}
                            />
                        )}
                        
                        <RightPanel
                            isEmbedded={isEmbedMode}
                            selectedNode={selectedNode as CustomNode | Node<AnyNodeData> | null} // Use guarded state
                            selectedEdge={selectedEdgeObject} // Pass the found edge object
                            mode={mode}
                        />
                    </ReactFlow>
                </div>
            </div>
        </CanvasContext.Provider>
    );
};

export default Canvas;
